<template>
  <div class="error-page min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-950 dark:to-zinc-900">
    <div class="flex min-h-screen items-center justify-center px-4">
      <div class="w-full max-w-2xl text-center">
        <!-- Animation Container -->
        <div class="mb-8 flex justify-center">
          <div class="relative">
            <!-- Main Error Code with Gradient -->
            <h1 class="bg-gradient-to-r from-zinc-600 to-zinc-900 bg-clip-text text-8xl font-bold text-transparent md:text-9xl dark:from-zinc-300 dark:to-zinc-100">
              {{ error.statusCode }}
            </h1>

            <!-- Floating Icons -->
            <div class="absolute -left-4 -top-4 animate-bounce">
              <div class="rounded-full bg-zinc-200 p-3 shadow-lg dark:bg-zinc-800">
                <UiLucideIcon
                  :icon="AlertTriangle"
                  class="size-6 text-zinc-600 dark:text-zinc-400"
                />
              </div>
            </div>

            <div
              class="absolute -right-6 -top-2 animate-bounce"
              style="animation-delay: 0.5s;"
            >
              <div class="rounded-full bg-zinc-200 p-2 shadow-lg dark:bg-zinc-800">
                <UiLucideIcon
                  :icon="Search"
                  class="size-4 text-zinc-600 dark:text-zinc-400"
                />
              </div>
            </div>

            <div
              class="absolute -bottom-2 left-1/2 -translate-x-1/2 animate-bounce"
              style="animation-delay: 1s;"
            >
              <div class="rounded-full bg-zinc-200 p-2 shadow-lg dark:bg-zinc-800">
                <UiLucideIcon
                  :icon="MapPin"
                  class="size-4 text-zinc-600 dark:text-zinc-400"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div class="mb-8 space-y-4">
          <h2 class="text-2xl font-semibold text-zinc-900 md:text-3xl dark:text-zinc-100">
            {{ $t(`pages.error.titles.${error.statusCode}`, $t('pages.error.titles.default')) }}
          </h2>
          <div class="mx-auto max-w-md space-y-2">
            <p class="text-lg text-zinc-600 dark:text-zinc-400">
              {{ $t(`pages.error.descriptions.${error.statusCode}`, $t('pages.error.descriptions.default')) }}
            </p>
            <!-- Error details -->
            <div
              v-if="error.message"
              class="rounded-lg bg-zinc-100 p-3 dark:bg-zinc-800"
            >
              <p class="mb-1 text-xs text-zinc-500 dark:text-zinc-400">
                {{ $t('pages.error.details.label') }}
              </p>
              <code class="block overflow-hidden break-all text-xs text-zinc-700 dark:text-zinc-300">
                {{ error.message }}
              </code>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col gap-4 sm:flex-row sm:justify-center">
          <Button
            :label="$t('pages.error.actions.home')"
            size="large"
            class="px-8 py-3"
            @click="goHome"
          >
            <template #icon>
              <UiLucideIcon :icon="Home" />
            </template>
          </Button>
        </div>

        <!-- TODO: Add documentation and support links -->

        <!-- Decorative Elements -->
        <div class="absolute inset-0 -z-10 overflow-hidden">
          <div class="absolute -right-40 -top-40 size-80 rounded-full bg-gradient-to-br from-zinc-200/20 to-zinc-300/20 blur-3xl dark:from-zinc-700/20 dark:to-zinc-800/20" />
          <div class="absolute -bottom-40 -left-40 size-80 rounded-full bg-gradient-to-tr from-zinc-200/20 to-zinc-300/20 blur-3xl dark:from-zinc-700/20 dark:to-zinc-800/20" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AlertTriangle, Home, MapPin, Search } from 'lucide-vue-next'
import { AppRoutes } from '~/../genesis/constants/routes'

const props = defineProps<{
  error: {
    statusCode: number
    statusMessage: string
    message: string
  }
}>()

const { t } = useI18n()
useHead({
  title: t('pages.error.head.title', { statusCode: props.error.statusCode }),
  meta: [
    { name: 'description', content: t('pages.error.head.description', { statusCode: props.error.statusCode }) },
  ],
})

const goHome = () => {
  navigateTo(AppRoutes.SelectOrganization)
}

onBeforeUnmount(() => {
  clearError()
})
</script>

<style scoped>
.error-page {
  position: relative;
}

/* Custom animations */
@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .error-page h1 {
    font-size: 4rem;
  }
}
</style>
